import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, Bookmark } from "lucide-react";
import TierSelector, { parseMultiTier, serializeMultiTier } from "./TierSelector";
import { TEAM_CATEGORY_CHOICES } from "./FriendlyFilterBar";
import CustomDateTimePicker from "../common/CustomDateTimePicker";
import { isoToDate, dateToISO } from "../../utils/friendlyDates";

// Mobile filters bottom sheet (design Frame 7.2). The desktop keeps the inline
// filter-chip row; on mobile it collapses behind a "Filters (N)" trigger and opens
// here as sections. Same state/setters as the page — presentation only. All targets
// ≥44px for one-thumb use.
const Row = ({ label, hint, children }) => (
  <div className="py-3 border-b border-[#EEF1F6] last:border-0">
    <div className="flex items-baseline justify-between mb-2">
      <p className="text-[13px] font-semibold text-[#14263D]">{label}</p>
      {hint && <span className="text-[11px] text-[#98A2B3]">{hint}</span>}
    </div>
    {children}
  </div>
);

const FriendlyFilterSheet = ({
  open, onClose, count,
  filters, setFilter, setDateRange, clearFilters,
  savedOnly, toggleSavedOnly, savedCount = 0, countries = [],
}) => {
  const [countryQuery, setCountryQuery] = useState("");
  if (!open) return null;
  const tierVal = parseMultiTier(filters.tier);

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end bg-[rgba(17,24,39,0.4)]" role="dialog" aria-modal="true">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full bg-white rounded-t-2xl max-h-[90vh] flex flex-col shadow-[0_-8px_30px_rgba(16,24,40,0.2)]">
        <div className="flex justify-center pt-2.5 pb-1" aria-hidden="true"><span className="w-9 h-1 rounded-full bg-[#E4E9F1]" /></div>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#EEF1F6]">
          <h2 className="text-[16px] font-bold text-[#14263D]">Filters</h2>
          <button onClick={clearFilters} className="text-[13px] font-semibold text-[#2283FF]">Reset</button>
        </div>

        <div className="flex-1 overflow-y-auto px-5">
          <Row label="Dates">
            <div className="flex flex-col gap-2">
              <CustomDateTimePicker value={isoToDate(filters.date_from)} onChange={(d) => setDateRange(dateToISO(d), filters.date_to)} placeholder="From" dateFormat="dd MMM yyyy" />
              <CustomDateTimePicker value={isoToDate(filters.date_to)} onChange={(d) => setDateRange(filters.date_from, dateToISO(d))} placeholder="To" dateFormat="dd MMM yyyy" minDate={isoToDate(filters.date_from)} />
            </div>
          </Row>

          <Row label="Location" hint="Falls back to country & region">
            <input value={countryQuery} onChange={(e) => setCountryQuery(e.target.value)} placeholder="Search countries…" className="w-full min-h-[44px] bg-[#F3F4F6] rounded-lg px-3 text-[14px] mb-2" />
            <div className="max-h-[180px] overflow-y-auto flex flex-col">
              {filters.country && (
                <button onClick={() => setFilter("country", "")} className="text-left min-h-[40px] px-2 rounded-md text-[14px] text-[#C4402F]">Clear ({filters.country})</button>
              )}
              {countries.filter((c) => (countryQuery ? (c.name || "").toLowerCase().includes(countryQuery.toLowerCase()) : true)).slice(0, 40).map((c) => (
                <button key={c.id} onClick={() => setFilter("country", c.name)} className={`flex items-center gap-2 text-left min-h-[40px] px-2 rounded-md text-[14px] ${filters.country === c.name ? "bg-[#F0F7FF] text-[#1466D8] font-semibold" : ""}`}>
                  {c.image_url && <img src={c.image_url} alt="" className="w-5 h-3.5 object-contain" referrerPolicy="no-referrer" />}
                  {c.name}
                </button>
              ))}
            </div>
          </Row>

          <Row label="Team">
            <div className="flex flex-wrap gap-2">
              {[{ value: "", label: "All teams" }, ...TEAM_CATEGORY_CHOICES].map((c) => (
                <button key={c.value || "all"} onClick={() => setFilter("team_category", c.value)}
                  className={`min-h-[44px] px-3 rounded-full border text-[13px] font-semibold ${filters.team_category === c.value ? "border-[#2283FF] bg-[#F0F7FF] text-[#1466D8]" : "border-[#E4E9F1] text-[#5A6B82]"}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </Row>

          <Row label="Opponent level" hint="Multi-select for a range">
            <TierSelector mode="multi" value={tierVal} onChange={(v) => setFilter("tier", serializeMultiTier(v))} showThreeOrLower showAny />
          </Row>

          <Row label="Saved">
            <button onClick={toggleSavedOnly} className={`min-h-[44px] px-4 rounded-lg border flex items-center gap-2 text-[14px] font-semibold ${savedOnly ? "border-[#2283FF] bg-[#F0F7FF] text-[#1466D8]" : "border-[#E4E9F1] text-[#5A6B82]"}`}>
              <Bookmark className="w-4 h-4" style={savedOnly ? { fill: "#2283FF", color: "#2283FF" } : undefined} />
              Only saved {savedCount > 0 && `(${savedCount})`}
            </button>
          </Row>
        </div>

        <div className="px-5 py-3 border-t border-[#EEF1F6]">
          <button onClick={onClose} className="w-full min-h-[48px] rounded-lg bg-[#2283FF] text-white text-[15px] font-semibold">
            Show {count} request{count === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default FriendlyFilterSheet;
